import { apiFetch } from "./api";
import { executeManifestQuery } from "./modules.service";

const MODULE_ID = "metas";

/** Um mês de referência e o peso que ele carrega no cenário, em pontos percentuais. */
export interface Referencia {
  ano: number;
  mes: number;
  peso: number;
}

/**
 * Situação do cenário no motor de cálculo do cliente — sempre definida pelo
 * servidor, nunca enviada pela tela. RASCUNHO/ENVIADO são editáveis e podem
 * ser calculados (`calcularCenario`); CALCULANDO é o motor rodando, não aceita
 * nenhuma ação e termina sozinho em CALCULADO ou de volta no estado anterior;
 * CALCULADO pode ser promovido (`promoverCenario`), e a promoção também é
 * assíncrona: PROMOVENDO é o merge rodando, que termina sozinho em PROMOVIDO ou
 * de volta em CALCULADO; PROMOVIDO não aceita mais
 * nenhuma das duas ações — exige um novo cenário ou, no caso de CALCULADO, um
 * novo cálculo.
 */
export type StatusCenario = "RASCUNHO" | "ENVIADO" | "CALCULANDO" | "CALCULADO" | "PROMOVENDO" | "PROMOVIDO";

/**
 * Cenário de cálculo da meta, no mesmo formato que o motor de metas do Flash
 * (PKG_FLASH_META_CALC, rodando no banco de cada cliente) consome — `id` é
 * numérico (o motor lê `id_cenario` como NUMBER no payload), não UUID.
 * Crescimento é sempre percentual e aplicado à companhia inteira: o motor não
 * tem modo "valor em R$", e a tela ainda não expõe crescimento por loja ou
 * categoria.
 */
export interface Cenario {
  id?: string;
  codSistema: string;
  descricao: string;
  anoMeta: number;
  mesMeta: number;
  crescimento: number;
  referencias: Referencia[];
  /** Recálculo ascendente: soma os ajustes MES também nos níveis acima (categoria, loja, companhia), além da propagação descendente do motor, que é sempre ativa. */
  propagarPai: boolean;
  /** Percentual das vendas do mês da meta esperado na 1ª e na 2ª quinzena (somam 100). */
  distribuicaoQuinzenal?: { perc1aQuinzena: number; perc2aQuinzena: number };
  /** Distribuição de Área (aba própria) que espalha a meta pelos dias; ausente = o cenário não usa nenhuma. */
  distribuicaoAreaId?: string;
  status?: StatusCenario;
  /** Só existe depois do primeiro cálculo — id da execução no motor do cliente (MANUAL.md §7), usado pra ler o resultado na revisão de metas. */
  idCalculoCliente?: number;
  /** Motivo da última tentativa de cálculo que falhou — o cálculo é assíncrono, então a falha não volta na resposta do disparo. */
  erroCalculo?: string;
}

/** Máximo de períodos históricos que o motor de cálculo aceita por cenário. */
export const MAX_PERIODOS_CENARIO = 3;

function executeMetasMethod<T>(methodName: string, params: unknown, clientId: string) {
  return apiFetch<T>(`/api/manifest/modules/${MODULE_ID}/methods/${methodName}/execute`, {
    method: "POST",
    body: JSON.stringify(params),
    headers: { "X-Client-ID": clientId },
  });
}

export const listCenarios = (clientId: string) => executeMetasMethod<Cenario[]>("list_cenarios", {}, clientId);

/** Sem `id` insere; com `id` substitui o cenário inteiro, referências incluídas. */
export const saveCenario = (cenario: Cenario, clientId: string) =>
  executeMetasMethod<Cenario>("save_cenario", cenario, clientId);

export const deleteCenario = (id: string, clientId: string) =>
  executeMetasMethod<{ id: string }>("delete_cenario", { id }, clientId);

/** Resposta de `calcularCenario`: o disparo aceito (status CALCULANDO), não o resultado. */
export interface CalculoIniciado {
  id: string;
  status: StatusCenario;
}

/** Resposta de `statusCalculo`: onde o cálculo está agora. */
export interface StatusCalculo {
  id: string;
  status: StatusCenario;
  /** Presente quando o cálculo terminou em CALCULADO. */
  idCalculoCliente?: number;
  /** Presente quando a última tentativa falhou — o cenário já voltou ao estado editável. */
  erroCalculo?: string;
}

/**
 * Dispara PKG_FLASH_META_CALC.CALCULAR no banco do cliente para este cenário e
 * responde na hora, sem esperar o motor: CALCULAR leva minutos, e o CloudFront
 * na frente da API corta a requisição em 30s (o que cancelava o cálculo no
 * meio). O cenário fica CALCULANDO e o acompanhamento é por `statusCalculo`
 * (metas.calcular_cenario, app-dono-product-runtime/internal/metas/cenarios.go).
 * Só é aceito para cenário em RASCUNHO ou ENVIADO.
 */
export const calcularCenario = (id: string, clientId: string) =>
  executeMetasMethod<CalculoIniciado>("calcular_cenario", { id }, clientId);

/**
 * Consulta o andamento do cálculo disparado por `calcularCenario`. Enquanto o
 * status for CALCULANDO o motor ainda está rodando; qualquer outro status é o
 * desfecho — CALCULADO com `idCalculoCliente`, ou o estado editável anterior
 * com `erroCalculo`.
 */
export const statusCalculo = (id: string, clientId: string) =>
  executeMetasMethod<StatusCalculo>("status_calculo", { id }, clientId);

/**
 * Promove o último cálculo do cenário (PKG_FLASH_META_CALC.PROMOVER,
 * MANUAL.md §7): mescla o resultado calculado nas metas efetivas do cliente.
 * Só é aceito para cenário CALCULADO — depois de promovido, um novo cálculo é
 * necessário para promover de novo.
 */
export const promoverCenario = (id: string, clientId: string) =>
  executeMetasMethod<{ id: string; status: StatusCenario }>("promover_cenario", { id }, clientId);

/** Nível do nó que um ajuste mira — mesmo vocabulário de tipo_nodo do ERP. */
export type TipoNodoAjuste = "EMPRESA" | "CATEGORIA" | "PRODUTO";

/** Métrica que o ajuste altera. */
export type IndicadorAjuste = "VENDA" | "CONTRIBUICAO" | "MARGEM" | "PERDA";

/** O ajuste mira o total do mês, ou um dia específico (exige `dataReferencia`). */
export type AlvoAjuste = "MES" | "DIA";

/** Como ler `valorInformado`: um valor absoluto ou uma variação percentual sobre o calculado. */
export type TipoValorAjuste = "VALOR" | "PERCENTUAL";

/**
 * Override manual de uma meta sobre o que o motor de cálculo sugeriu, no
 * mesmo formato que `APLICAR_AJUSTES` consome. Atenção: o motor só lê ajustes
 * no `CALCULAR`, pelo payload, e hoje `calcular_cenario` o envia com a lista
 * vazia e descarta os ajustes salvos ao concluir; `PROMOVER` só lê o resultado
 * calculado. Ou seja, o ajuste fica salvo para revisão na tela, mas ainda não
 * altera a meta promovida.
 * MetasManutencaoTab.tsx grava um ajuste por nó editado, no nível em que
 * foi feito (loja, categoria ou produto).
 */
export interface Ajuste {
  /** Loja a que o ajuste vale; ausente = todas (a categoria/produto existe uma vez por loja). */
  codEmpresa?: number | null;
  codigoNodo: string;
  tipoNodo: TipoNodoAjuste;
  indicador: IndicadorAjuste;
  alvo: AlvoAjuste;
  /** "AAAA-MM-DD"; obrigatório quando `alvo` é "DIA", ausente quando é "MES". */
  dataReferencia?: string;
  tipoValor: TipoValorAjuste;
  valorInformado: number;
  alteraTotalMensal?: boolean;
  justificativa?: string;
}

export const obterAjustes = (cenarioId: string, clientId: string) =>
  executeMetasMethod<Ajuste[]>("obter_ajustes", { cenarioId }, clientId);

/** Substitui todos os ajustes do cenário pelo conjunto informado, não um merge. */
export const salvarAjustes = (cenarioId: string, ajustes: Ajuste[], clientId: string) =>
  executeMetasMethod<Ajuste[]>("salvar_ajustes", { cenarioId, ajustes }, clientId);

/** Um ponto no histórico de edição de um nó: quem colocou `valor` (em reais) e quando. */
export interface HistoricoAjusteItem {
  usuario: string;
  /** ISO 8601. */
  data: string;
  valor: number;
}

/**
 * Histórico de um nó (VENDA/MES, o grão que esta tela grava) reconstruído a
 * partir da trilha de auditoria de `salvarAjustes` — cada chamada salva o
 * conjunto inteiro de ajustes ativos, não um diff, então o back reconstrói
 * as mudanças de valor comparando os eventos entre si (só entra aqui quando
 * o valor deste nó realmente mudou de um salvamento para o outro). Mais
 * recente primeiro.
 */
export const historicoAjuste = (
  cenarioId: string,
  codigoNodo: string,
  clientId: string,
  codEmpresa?: number | null,
  tipoNodo?: TipoNodoAjuste,
) =>
  executeMetasMethod<HistoricoAjusteItem[]>(
    "historico_ajuste",
    { cenarioId, codigoNodo, ...(codEmpresa != null ? { codEmpresa } : {}), ...(tipoNodo ? { tipoNodo } : {}) },
    clientId,
  );

/** Como uma regra entra no cálculo: só marca o período ou aplica a variação. */
export type Efeito = "referencia" | "percentual";

/** Tipo do evento, no mesmo texto que o back-end grava. */
export type TipoEvento = "Sazonalidade" | "Feriado";

/** Categoria sentinela usada quando o evento vale para a árvore inteira. */
export const CATEGORIA_TODAS = "all";

/** Regra de uma categoria (ou de toda a árvore, em CATEGORIA_TODAS). */
export interface RegraEvento {
  categoriaId: string;
  efeito: Efeito;
  percentual: number;
  observacoes: string;
}

/**
 * Um mês anterior em que o evento também se aplica, além do intervalo
 * contínuo (nunca no lugar dele) — o peso deste mês na distribuição, que
 * junto com os demais meses do evento tem que somar exatamente 100%, mesma
 * regra dos meses de referência de Cenário (Referencia.peso). Compartilhado
 * por todas as categorias que as regras do evento já selecionam (não varia
 * por categoria).
 */
export interface PeriodoEvento {
  ano: number;
  mes: number;
  percentual: number;
}

/** Feriado/sazonalidade no formato do Product Runtime. */
export interface Evento {
  id?: string;
  nome: string;
  tipo: TipoEvento;
  ativo: boolean;
  /** "AAAA-MM-DD". */
  dataInicial: string;
  dataFinal: string;
  repetirAnualmente: boolean;
  abrangencia: "global" | "especificas";
  /** Códigos das lojas no ERP; vazio quando a abrangência é global. */
  lojas: string[];
  escopoCategorias: "todas" | "especificas";
  regras: RegraEvento[];
  periodos: PeriodoEvento[];
}

export const listEventos = (clientId: string) => executeMetasMethod<Evento[]>("list_eventos", {}, clientId);

/** Sem `id` insere; com `id` substitui o evento inteiro, lojas e regras incluídas. */
export const saveEvento = (evento: Evento, clientId: string) =>
  executeMetasMethod<Evento>("save_evento", evento, clientId);

export const deleteEvento = (id: string, clientId: string) =>
  executeMetasMethod<{ id: string }>("delete_evento", { id }, clientId);

/** Método de distribuição diária que o motor de cálculo aceita. */
export type MetodoDistribuicaoDiaria = "DIA_EQUIVALENTE";

/** Um intervalo da meta que compara com outro período, em vez do dia equivalente. */
export interface DePara {
  /** "AAAA-MM-DD". */
  data_inicio: string;
  data_fim: string;
  comparacao_inicio: string;
}

/**
 * Bloco `distribuicao_diaria` do payload do cenário, com as chaves exatamente
 * como o motor as lê (snake_case) — por isso nada é convertido na ida e na volta.
 * `comparacao_inicio`/`comparacao_fim` nulos deixam o motor usar só
 * `data_referencia` e `dias_retroativos`.
 */
export interface DistribuicaoDiaria {
  metodo: MetodoDistribuicaoDiaria;
  data_referencia: string;
  dias_retroativos: number;
  comparacao_inicio: string | null;
  comparacao_fim: string | null;
  de_para: DePara[];
}

/** Distribuição de Área no formato do Product Runtime. */
export interface DistribuicaoArea {
  id?: string;
  nome: string;
  ativo: boolean;
  distribuicao_diaria: DistribuicaoDiaria;
}

export const listDistribuicoesArea = (clientId: string) =>
  executeMetasMethod<DistribuicaoArea[]>("list_distribuicoes_area", {}, clientId);

/** Sem `id` insere; com `id` substitui a distribuição inteira, De/Para incluído. */
export const saveDistribuicaoArea = (distribuicao: DistribuicaoArea, clientId: string) =>
  executeMetasMethod<DistribuicaoArea>("save_distribuicao_area", distribuicao, clientId);

export const deleteDistribuicaoArea = (id: string, clientId: string) =>
  executeMetasMethod<{ id: string }>("delete_distribuicao_area", { id }, clientId);

/** Regra de contribuição de uma categoria (ou de toda a árvore, em CATEGORIA_TODAS). */
export interface RegraContribuicao {
  categoriaId: string;
  percentual: number;
  observacoes: string;
}

/**
 * Cadastro de contribuição no formato do Product Runtime: vale para um mês de
 * referência (ano/mês separados, como o mês da meta do cenário) e para o
 * cliente inteiro — sem escopo por loja, sempre uma configuração geral — e
 * guarda o percentual de contribuição de cada categoria em escopo. Ao
 * calcular um cenário cujo mês da meta bate com anoReferencia/mesReferencia,
 * o Product Runtime dobra cada regra ativa direto no `crescimento` do payload
 * (nivel_alvo N2, indicador CONTRIBUICAO, codigo_nodo = categoriaId) — não
 * existe uma seção separada de contribuições no payload, e a linha não repete
 * o ano/mês: o motor já lê isso do ano_meta/mes_meta no topo do JSON.
 */
export interface Contribuicao {
  id?: string;
  nome: string;
  ativo: boolean;
  anoReferencia: number;
  mesReferencia: number;
  escopoCategorias: "todas" | "especificas";
  regras: RegraContribuicao[];
}

export const listContribuicoes = (clientId: string) =>
  executeMetasMethod<Contribuicao[]>("list_contribuicoes", {}, clientId);

/** Sem `id` insere; com `id` substitui o cadastro inteiro, regras incluídas. */
export const saveContribuicao = (contribuicao: Contribuicao, clientId: string) =>
  executeMetasMethod<Contribuicao>("save_contribuicao", contribuicao, clientId);

export const deleteContribuicao = (id: string, clientId: string) =>
  executeMetasMethod<{ id: string }>("delete_contribuicao", { id }, clientId);

/** Um nó da mercadologia do ERP: uma categoria de qualquer nível ou um produto. */
export interface CategoriaMercadologica {
  /** Código da categoria/produto no ERP — é o que a regra do evento grava. */
  id: string;
  nome: string;
  /** Produto: último nível da árvore, não abre filhos. */
  folha: boolean;
}

/** Uma linha da consulta de mercadologia, como o ERP devolve (tudo texto). */
interface MercadologiaRaw {
  codigo: string | null;
  nome: string | null;
  /** "PRODUTO" no último nível da árvore; o nome do nível nos demais. */
  tipo: string | null;
}

/**
 * Filhos de `pai` — o primeiro nível quando `pai` é nulo. A mercadologia é uma
 * consulta do próprio módulo de metas (`metas.mercadologia`): o flash de vendas
 * tem uma equivalente, mas depender dela amarraria as metas à contratação de
 * outro módulo, já que a execução valida a habilitação do módulo da URL.
 *
 * `codEmpresa` nulo (padrão) devolve a árvore consolidada do cliente, como as
 * regras de eventos sempre usaram; uma loja específica escopa a árvore a ela
 * — é o que a revisão de metas por loja precisa.
 */
export const listCategorias = (clientId: string, pai: string | null = null, codEmpresa: string | null = null) =>
  executeManifestQuery<MercadologiaRaw[]>(
    MODULE_ID,
    "mercadologia",
    { codigo_categoria_pai: pai, cod_empresa: codEmpresa },
    clientId,
  ).then((linhas) =>
    linhas
      .filter((linha): linha is MercadologiaRaw & { codigo: string; nome: string } =>
        Boolean(linha.codigo && linha.nome),
      )
      .map<CategoriaMercadologica>((linha) => ({
        id: linha.codigo,
        nome: linha.nome,
        folha: linha.tipo === "PRODUTO",
      })),
  );

/** Um nó devolvido por `localizarCategorias`: a própria seleção ou um ancestral dela. */
export interface CategoriaLocalizada extends CategoriaMercadologica {
  /** Código do pai imediato; nulo quando o nó é raiz (N1) da mercadologia. */
  codigoPai: string | null;
}

/** Uma linha de `mercadologia_localizar`, como o ERP devolve (tudo texto). */
interface MercadologiaLocalizarRaw {
  codigo: string | null;
  nome: string | null;
  tipo: string | null;
  codigo_pai: string | null;
}

/**
 * Resolve `codigos` salvos (categoria ou produto) para nome e ancestrais, sem
 * percorrer a árvore nível a nível como `listCategorias` exigiria — o que um
 * código a alguns níveis do topo, ainda não carregado, deixaria aparecer como
 * o próprio código na tela de edição. Devolve cada código pedido mais todo
 * ancestral até a raiz, uma vez cada mesmo que vários códigos compartilhem um
 * ancestral; ver o comentário equivalente em metas.module.ts.
 */
export const localizarCategorias = (clientId: string, codigos: string[], codEmpresa: string | null = null) => {
  if (codigos.length === 0) return Promise.resolve<CategoriaLocalizada[]>([]);
  return executeManifestQuery<MercadologiaLocalizarRaw[]>(
    MODULE_ID,
    "mercadologia_localizar",
    { codigos, cod_empresa: codEmpresa },
    clientId,
  ).then((linhas) =>
    linhas
      .filter((linha): linha is MercadologiaLocalizarRaw & { codigo: string; nome: string; tipo: string } =>
        Boolean(linha.codigo && linha.nome && linha.tipo),
      )
      .map<CategoriaLocalizada>((linha) => ({
        id: linha.codigo,
        nome: linha.nome,
        folha: linha.tipo === "PRODUTO",
        codigoPai: linha.codigo_pai,
      })),
  );
};

/** Quantos meses fechados a revisão mostra ao lado da meta — até o mesmo mês do ano anterior, inclusive. */
export const MESES_HISTORICO_REVISAO = 12;

/** Raiz virtual de `listRevisaoNodos`: a companhia consolidada (nó -1 do motor), cujos filhos são as lojas. */
export const RAIZ_REVISAO = "-1";

/** Uma linha de `revisao_nodos`/`revisao_localizar`, como o ERP devolve (tudo texto/número). */
interface RevisaoNodoRaw {
  id_nodo: string | number | null;
  id_nodo_pai: string | number | null;
  tipo_nodo: string | null;
  codigo: string | null;
  nome: string | null;
  cod_empresa: string | number | null;
  nivel: number | string | null;
  alvo: number | string | null;
  valor_mensal_calculado: number | string | null;
  valor_mensal_inteiro?: number | string | null;
  valor_mensal_sugerido: number | string | null;
  valor_contribuicao_calculado: number | string | null;
  perc_contribuicao_enviado: number | string | null;
  valor_perda_calculado: number | string | null;
  [mes: `mes_${string}`]: number | string | null;
}

/**
 * Um nó (loja, categoria ou produto) da revisão de um cálculo, com o valor
 * mensal que o motor produziu para ele (indicador VENDA — a tela ainda não
 * expõe os demais) e os `MESES_HISTORICO_REVISAO` meses de venda realizada
 * anteriores ao mês da meta. O motor grava uma linha por nível, então o
 * valor de categoria/loja é dele mesmo, não uma soma de produtos — e a
 * árvore é carregada um nível por vez (MetasManutencaoTab.tsx).
 */
export interface RevisaoNodo {
  idNodo: string;
  /** Nulo na loja: o pai dela é a companhia consolidada, que não existe como nó. */
  idNodoPai: string | null;
  tipoNodo: TipoNodoAjuste;
  codigo: string;
  nome: string;
  /** Empresa dona do nó; ausente só para um eventual nó agregado sem empresa. */
  codEmpresa: string | null;
  /** `nivel` do ERP — o absoluto de tb_flash_nodo (loja = 1). A árvore da tela não usa: calcula a profundidade pelo pai (`incorporar`/`aplicarCarga`). */
  nivelRelativo: number;
  /** Em `localizarRevisao`: o nó foi um acerto (vs. só ancestral de um). */
  alvo: boolean;
  /** Em reais, não centavos — mesma unidade que a coluna Oracle devolve. */
  valorMensalCalculado: number;
  /**
   * `valorMensalCalculado` em reais inteiros, já com os filhos fechando no
   * pai — resolvido no ERP sobre a árvore inteira, então não depende do que a
   * tela já carregou. `null` quando a query não devolve (manifesto antigo, nó
   * mais fundo que o ERP cobre): a tela arredonda ela mesma.
   */
  valorMensalInteiro?: number | null;
  valorMensalSugerido: number;
  /** Quanto do valor calculado deste nó é a contribuição do ajuste manual (0 sem ajuste na subárvore) — vem pronto do motor. */
  valorContribuicaoCalculado: number;
  /** % de contribuição resolvido pelo motor pra este nó (15.05 = 15,05%); `null` quando caiu no fallback de margem histórica (MANUAL.md §3.3). */
  percContribuicaoEnviado: number | null;
  /** Meta de perda que o motor calculou para este nó (indicador PERDA); 0 quando o nó não tem perda no cálculo. */
  valorPerdaCalculado: number;
  /** Venda realizada em cada um dos meses da janela, do mais antigo ao mais recente; 0 sem movimento. */
  historico: number[];
}

/** Janela de meses que a revisão pede: "AAAA-MM-DD" do primeiro dia do mais antigo ao último dia do mais recente. */
export interface JanelaHistorico {
  dataInicio: string;
  dataFim: string;
}

const mapearRevisaoNodo = (linhas: RevisaoNodoRaw[]): RevisaoNodo[] =>
  linhas
    .filter(
      (
        linha,
      ): linha is RevisaoNodoRaw & { id_nodo: string | number; tipo_nodo: string; codigo: string; nome: string } =>
        linha.id_nodo !== null && linha.id_nodo !== undefined && !!linha.tipo_nodo && !!linha.codigo && !!linha.nome,
    )
    .map<RevisaoNodo>((linha) => ({
      idNodo: String(linha.id_nodo),
      idNodoPai:
        linha.tipo_nodo === "EMPRESA" || linha.id_nodo_pai === null || linha.id_nodo_pai === undefined
          ? null
          : String(linha.id_nodo_pai),
      tipoNodo: linha.tipo_nodo as TipoNodoAjuste,
      codigo: linha.codigo,
      nome: linha.nome,
      codEmpresa: linha.cod_empresa === null || linha.cod_empresa === undefined ? null : String(linha.cod_empresa),
      nivelRelativo: Number(linha.nivel ?? 0),
      alvo: Number(linha.alvo ?? 0) === 1,
      valorMensalCalculado: Number(linha.valor_mensal_calculado ?? 0),
      valorMensalInteiro:
        linha.valor_mensal_inteiro === null || linha.valor_mensal_inteiro === undefined
          ? null
          : Number(linha.valor_mensal_inteiro),
      valorMensalSugerido: Number(linha.valor_mensal_sugerido ?? linha.valor_mensal_calculado ?? 0),
      valorContribuicaoCalculado: Number(linha.valor_contribuicao_calculado ?? 0),
      valorPerdaCalculado: Number(linha.valor_perda_calculado ?? 0),
      percContribuicaoEnviado:
        linha.perc_contribuicao_enviado === null || linha.perc_contribuicao_enviado === undefined
          ? null
          : Number(linha.perc_contribuicao_enviado),
      historico: Array.from({ length: MESES_HISTORICO_REVISAO }, (_, i) =>
        Number(linha[`mes_${String(i + 1).padStart(2, "0")}`] ?? 0),
      ),
    }));

/**
 * Os descendentes de `idNodoRaiz` até `niveis` abaixo dele (1 = só os filhos
 * diretos), para um cálculo já concluído — `idCalculo` vem de
 * `Cenario.idCalculoCliente`. `RAIZ_REVISAO` devolve as lojas.
 */
export const listRevisaoNodos = (
  clientId: string,
  idCalculo: number,
  idNodoRaiz: string,
  niveis: number,
  janela: JanelaHistorico,
) =>
  executeManifestQuery<RevisaoNodoRaw[]>(
    MODULE_ID,
    "revisao_nodos",
    {
      id_calculo: idCalculo,
      id_nodo_raiz: Number(idNodoRaiz),
      niveis,
      data_inicio: janela.dataInicio,
      data_fim: janela.dataFim,
    },
    clientId,
  ).then(mapearRevisaoNodo);

/**
 * Último nível de categoria da árvore (N1 = 1) que o usuário enxerga — o
 * produto vem abaixo dele. `null` quando o ERP não tem categoria nenhuma.
 */
export const getProfundidadeRevisao = (clientId: string) =>
  executeManifestQuery<{ niveis_categoria: number | string | null }[]>(
    MODULE_ID,
    "revisao_profundidade",
    {},
    clientId,
  ).then((linhas) => {
    const niveis = Number(linhas[0]?.niveis_categoria);
    return Number.isFinite(niveis) && niveis > 0 ? niveis : null;
  });

/**
 * Localiza nós por `termo` (nome ou código, até 200 acertos) ou por uma lista
 * de `codigos` (os ajustes salvos) e devolve cada acerto (`alvo`) junto com
 * todos os ancestrais até a loja — o suficiente para abrir o caminho até ele
 * sem carregar os irmãos. Exatamente um dos dois critérios deve vir.
 */
export const localizarRevisao = (
  clientId: string,
  idCalculo: number,
  criterio: { termo: string } | { codigos: string[] },
  janela: JanelaHistorico,
) =>
  executeManifestQuery<RevisaoNodoRaw[]>(
    MODULE_ID,
    "revisao_localizar",
    {
      id_calculo: idCalculo,
      termo: "termo" in criterio ? criterio.termo : null,
      codigos: "codigos" in criterio ? criterio.codigos : null,
      data_inicio: janela.dataInicio,
      data_fim: janela.dataFim,
    },
    clientId,
  ).then(mapearRevisaoNodo);

/** Uma linha de `relatorio_comprador`, como o ERP devolve. */
interface RelatorioCompradorRaw {
  cod_empresa: string | number | null;
  id_nodo_escala: string | number;
  nome_n1: string | null;
  nome_n2: string | null;
  nome_n3: string | null;
  cod_comprador: string | number | null;
  nome_comprador: string | null;
  valor_venda: number | string | null;
  valor_contribuicao: number | string | null;
  valor_perda: number | string | null;
}

/** Soma dos produtos de um cálculo por loja × N3 × comprador — o motor, sem ajustes manuais. */
export interface LinhaCompradorCalculada {
  codEmpresa: number | null;
  /** id_nodo do N3 (ou do ancestral mais fundo, se o produto não tem N3) na árvore da revisão — chave pra achar a razão ajustada ÷ calculada. */
  idEscala: string;
  departamento: string;
  secao: string;
  subsecao: string;
  /** Vazio quando o produto não tem comprador mapeado. */
  comprador: string;
  venda: number;
  contribuicao: number;
  perda: number;
}

/**
 * Metas calculadas de um cálculo, por loja × N3 × comprador, somando os
 * produtos (o comprador é atributo do produto). Vazio quando o cenário não
 * inclui produto no cálculo.
 */
export const listRelatorioComprador = (clientId: string, idCalculo: number, codEmpresa: number | null = null) =>
  executeManifestQuery<RelatorioCompradorRaw[]>(
    MODULE_ID,
    "relatorio_comprador",
    { id_calculo: idCalculo, cod_empresa: codEmpresa },
    clientId,
  ).then((linhas) =>
    linhas.map<LinhaCompradorCalculada>((linha) => ({
      codEmpresa:
        linha.cod_empresa !== null && Number.isFinite(Number(linha.cod_empresa)) ? Number(linha.cod_empresa) : null,
      idEscala: String(linha.id_nodo_escala),
      departamento: linha.nome_n1 ?? "",
      secao: linha.nome_n2 ?? "",
      subsecao: linha.nome_n3 ?? "",
      comprador: linha.nome_comprador ?? "",
      venda: Number(linha.valor_venda ?? 0),
      contribuicao: Number(linha.valor_contribuicao ?? 0),
      perda: Number(linha.valor_perda ?? 0),
    })),
  );

/** Ações que um responsável pode ter no fluxo de aprovação das metas; um usuário acumula quantas quiser. */
export type AcaoResponsavel = "ADMINISTRAR" | "CALCULAR" | "REVISAR" | "APROVAR" | "PUBLICAR" | "REABRIR";

/**
 * Recorte de lojas × categorias de uma ação de escopo. `lojas` são códigos do
 * ERP (`Company.erp_code`); `categorias` são os códigos mercadológicos marcados
 * explicitamente — marcar um pai grava o pai e todos os descendentes, então
 * uma categoria criada depois não entra sozinha no escopo de ninguém.
 */
export interface EscopoResponsavel {
  lojas: string[];
  categorias: string[];
}

/**
 * Permissão do fluxo de aprovação: um usuário numa ação, com o escopo dela.
 * Cada ação do usuário tem o próprio escopo (vazio nas que não são de escopo).
 * O cadastro é global por cliente — vale para todo cálculo, e cada ciclo de
 * aprovação tira uma cópia no início.
 */
export interface Responsavel extends EscopoResponsavel {
  userId: string;
  acao: AcaoResponsavel;
}

export const listResponsaveis = (clientId: string) =>
  executeMetasMethod<Responsavel[]>("listar_responsaveis", {}, clientId);

/**
 * Se o cliente usa o fluxo de aprovação (metas.fluxo_obter). `ativo` é o que de
 * fato governa os cenários: habilitado e com responsáveis cadastrados.
 * `bloqueios` só vem para quem pode alternar.
 */
export interface ConfiguracaoFluxo {
  habilitado: boolean;
  ativo: boolean;
  podeAlternar: boolean;
  bloqueios: string[];
}

export const obterFluxo = (clientId: string) => executeMetasMethod<ConfiguracaoFluxo>("fluxo_obter", {}, clientId);

/** Liga ou desliga o fluxo de aprovação; recusado com ciclo aberto ou período calculado não publicado. */
export const alterarFluxo = (habilitado: boolean, clientId: string) =>
  executeMetasMethod<{ habilitado: boolean }>("fluxo_alterar", { habilitado }, clientId);

/** Cria ou substitui a permissão do usuário na ação (upsert por `userId` + `acao`); as outras ações dele não mudam. */
export const saveResponsavel = (responsavel: Responsavel, clientId: string) =>
  executeMetasMethod<Responsavel>("salvar_responsavel", responsavel, clientId);

export const deleteResponsavel = (userId: string, acao: AcaoResponsavel, clientId: string) =>
  executeMetasMethod<{ userId: string; acao: AcaoResponsavel }>("remover_responsavel", { userId, acao }, clientId);

/**
 * Etapa do escopo de um responsável no ciclo de aprovação. Cada um só passa
 * pelas etapas que as suas ações cobrem; `NA` é quem só administra, publica ou
 * reabre e não tem escopo a calcular, revisar ou aprovar.
 */
export type EtapaCiclo = "NA" | "CALCULO" | "REVISAO" | "APROVACAO" | "CONCLUIDA";

/** O responsável como o ciclo o copiou ao abrir, com a etapa do escopo dele. */
export interface ParteCiclo {
  id: string;
  userId: string;
  /** Escopo de cada ação de escopo (calcular, revisar, aprovar) que ele tem. */
  escopos: Partial<Record<AcaoResponsavel, EscopoResponsavel>>;
  acoes: AcaoResponsavel[];
  etapa: EtapaCiclo;
  /** Versão que a tela viu: `cicloAgir` a devolve, e o servidor recusa se outro usuário já agiu. */
  versao: number;
}

export interface CicloInfo {
  id: string;
  numero: number;
  status: "ABERTO" | "PUBLICADO";
  abertoPor: string;
  /** Preenchida quando o ciclo nasceu de uma reabertura. */
  justificativa?: string;
  /** ISO 8601. */
  criadoEm: string;
  publicadoEm?: string;
}

/** Um passo do histórico do ciclo: quem mudou a etapa do escopo de quem, de onde para onde. */
export interface ManifestacaoCiclo {
  id: string;
  responsavelUserId: string;
  autorId: string;
  acao: string;
  etapaAnterior: EtapaCiclo;
  etapaNova: EtapaCiclo;
  observacao: string;
  /** Consequência de outra ação (valores alterados, escopo devolvido ao cálculo), não decisão do responsável. */
  automatica: boolean;
  /** ISO 8601. */
  data: string;
}

/**
 * Situação do ciclo de aprovação de um cenário. `fluxoAtivo` é o cliente ter
 * responsáveis cadastrados: a partir daí calcular, ajustar e publicar seguem o
 * ciclo (metas.ciclo_obter, app-dono-product-runtime/internal/metas/ciclos.go).
 */
export interface EstadoCiclo {
  fluxoAtivo: boolean;
  /** Nulo quando não há ciclo aberto. */
  ciclo: CicloInfo | null;
  /** O mais recente quando não há nenhum aberto (publicado). */
  ultimoCiclo?: CicloInfo;
  partes: ParteCiclo[];
  /** Ações do usuário logado — todas, para o admin da plataforma. */
  minhasAcoes: AcaoResponsavel[];
  /** O que impede publicar; vazio quando `prontoParaPublicar`. */
  bloqueios: string[];
  prontoParaPublicar: boolean;
  /** Mais recente primeiro. */
  historico: ManifestacaoCiclo[];
}

export const cicloObter = (cenarioId: string, clientId: string) =>
  executeMetasMethod<EstadoCiclo>("ciclo_obter", { cenarioId }, clientId);

/** Um ciclo do cenário, aberto ou já publicado, com o histórico dele (mais recente primeiro). */
export interface CicloComHistorico extends CicloInfo {
  historico: ManifestacaoCiclo[];
}

/** Todos os ciclos do cenário, o mais recente primeiro (metas.ciclo_historico). */
export const cicloHistorico = (cenarioId: string, clientId: string) =>
  executeMetasMethod<{ ciclos: CicloComHistorico[] }>("ciclo_historico", { cenarioId }, clientId);

/** Abre o ciclo de um cenário CALCULADO, copiando os responsáveis cadastrados agora. */
export const cicloIniciar = (cenarioId: string, clientId: string) =>
  executeMetasMethod<{ id: string; numero: number }>("ciclo_iniciar", { cenarioId }, clientId);

/** Ações sobre o escopo de um responsável. Só o dono revisa e aprova o próprio; quem administra direciona. */
export type AcaoCiclo = "ENCAMINHAR" | "VALIDAR" | "APROVAR" | "DEVOLVER" | "DIRECIONAR";

export interface AgirCiclo {
  cenarioId: string;
  /** `ParteCiclo.id`. */
  responsavelId: string;
  acao: AcaoCiclo;
  /** Obrigatória em DEVOLVER e DIRECIONAR. */
  observacao?: string;
  versao: number;
  /** Só em DIRECIONAR: CALCULO, REVISAO ou APROVACAO. */
  paraEtapa?: EtapaCiclo;
}

export const cicloAgir = (params: AgirCiclo, clientId: string) =>
  executeMetasMethod<{ etapa: EtapaCiclo }>("ciclo_agir", params, clientId);

/**
 * Reinicia o ciclo no cálculo, com justificativa: num ciclo aberto devolve
 * todos os escopos à etapa inicial; num cenário publicado abre o ciclo seguinte
 * (as metas publicadas seguem vigentes até a nova publicação).
 */
export const cicloReabrir = (cenarioId: string, justificativa: string, clientId: string) =>
  executeMetasMethod<{ id: string }>("ciclo_reabrir", { cenarioId, justificativa }, clientId);
